import config from 'config'
import React from 'react'
import * as t from '../types'

type Props = {
  images: t.Images
  children: React.ReactElement
}

type Gallery = {
  index: number
  totalThumbs: number
  setIndex: (i: number) => void
  thumbnails: t.Thumbnails
  mainImage: t.MainImage
}

const Context = React.createContext<Gallery>({
  index: 0,
  totalThumbs: 1,
  setIndex: () => null,
  thumbnails: [],
  mainImage: {
    url: '',
    classes: [],
    type: 'image'
  }
})

export default function useGalleryContext() {
  return React.useContext(Context)
}

export function GalleryProvider(props: Props) {
  const [index, _setIndex] = React.useState<number>(0)
  const [totalThumbs, setTotalThumbs] = React.useState<number>(1)
  const [mainImage, setMainImage] = React.useState<t.MainImage>({
    url: config.modules.cloudinary.fallback_product_picture,
    classes: ['ASSET_FS'],
    type: 'image'
  })

  const [thumbnails, setThumbnails] = React.useState<t.Thumbnails>([])

  const setIndex = React.useCallback(
    (i: number) => {
      if (i > totalThumbs - 1) _setIndex(0)
      else if (i < 0) _setIndex(totalThumbs - 1)
      else _setIndex(i)
    },
    [totalThumbs]
  )

  React.useEffect(() => {
    setTotalThumbs(
      props.images.imageWeb.length + (props.images.video?.length || 0)
    )
  }, [props.images])

  // set main image logic
  React.useEffect(() => {
    if (hasNoAvailableContent(props.images)) {
      setMainImage({
        url: '',
        classes: ['ASSET_FS'],
        type: 'image'
      })
    } else {
      if (!thumbnails[index]) return
      if (thumbnails[index].type === 'video') {
        setMainImage({
          url: `https://res.cloudinary.com/lusini/image/upload/v1626872250/lusini-play-image.jpg`,
          classes: ['ASSET_FS'],
          type: 'video',
          wistiaID:
            props.images.video?.[thumbnails[index].videoIndex || 0]?.wistiaID ||
            ''
        })
      } else {
        if (!props.images.imageWeb[index]?.url) return
        setMainImage({
          url: props.images.imageWeb[index].url,
          classes: ['ASSET_FS'],
          type: 'image'
        })
      }
    }
  }, [index, props.images, thumbnails])

  // set inital main image + thumbnails when variant changes
  React.useEffect(() => {
    setIndex(0)
    if (hasNoAvailableContent(props.images)) {
      setMainImage({
        url: config.modules.cloudinary.fallback_product_picture,
        classes: ['ASSET_FS'],
        type: 'image'
      })
      setThumbnails([])
    } else {
      if (props.images.imageWeb[0]?.url) {
        setMainImage({
          url: props.images.imageWeb[0].url,
          classes: ['ASSET_FS'],
          type: 'image'
        })
      } else {
        setMainImage({
          url: `https://res.cloudinary.com/lusini/image/upload/v1626872250/lusini-play-image.jpg`,
          classes: ['ASSET_FS'],
          type: 'video',
          wistiaID: props.images.video?.[0].wistiaID || ''
        })
      }

      /**
       * Creating all possible thumbnails
       * 1. image Thumbnails
       * 2. video Thumbnails
       * */
      setThumbnails([
        ...(props.images.imageWeb?.map((img) => ({
          ...img,
          type: 'image' as const
        })) || []),
        ...(props.images.video?.map((v, i) => ({
          url: `https://res.cloudinary.com/lusini/image/upload/v1626872250/lusini-play-image.jpg`,
          classes: v.classes || ['ASSET_FS'],
          type: 'video' as const,
          videoIndex: i
        })) || [])
      ])
    }
  }, [props.images])

  const gallery: Gallery = {
    index,
    setIndex,
    totalThumbs,
    thumbnails,
    mainImage
  }
  return <Context.Provider value={gallery}>{props.children}</Context.Provider>
}

// checks if current variant has available images or videos
const hasNoAvailableContent = (images: t.Images) => {
  // first check if no image is available (or is dummy)
  if (
    images.imageWeb.length === 0 ||
    (images.imageWeb.length > 0 &&
      (images.imageWeb[0].classes[0] === 'DUMMY' ||
        images.imageWeb[0].url === ''))
  ) {
    // second check if no video is available
    if (
      !images.video ||
      images.video.length === 0 ||
      images.video.every((vid) => !vid.wistiaID)
    )
      return true
  }
  //fallback
  return false
}
